import { FC, FormEvent, useEffect, ChangeEvent, useState } from 'react';
import { FormikProps } from 'formik';
import { State } from '.';
import { SignupGrid, SignupGridItem } from '../../../lib/signup/styles';
import {
  IGroupHeader,
  FancyInput,
  InputSize,
  PrimaryButton,
  ButtonSize,
  IDetail,
  OSmallParagraph,
  defaultColors,
  Box,
  Checkbox,
  Input,
  OXSmallParagraph,
} from '@lifechurch/react-ion';
import { useQuery } from '@apollo/client';
import { AREAS_OF_MINISTRY } from '../../../lib/queries';
import { SignupAreaOfMinistriesInput } from '../../../../__generated__/globalTypes';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/dist/client/router';
import { clientCachedSubscriber } from '../../../lib/withApollo';
import { FormErrors } from '../../FormErrors';

interface GeoWhere {
  country_code: string;
  country_name: string;
  region: string;
  city: string;
  latitude: string;
  longitude: string;
  ip: string;
  gdpr: boolean;
}

const UserSignupForm: FC<FormikProps<State>> = props => {
  const router = useRouter();
  const { values, setValues, errors, setErrors, handleChange, validateForm } = props;
  const [geoWhereData, setGeoWhereData] = useState<GeoWhere | undefined>(undefined);
  const { data: ministryData } = useQuery(AREAS_OF_MINISTRY);
  const [otherMinistry, setOtherMinistry] = useState<SignupAreaOfMinistriesInput>({
    id: uuidv4(),
    label: '',
    key: '',
  });
  const [otherMinistryChecked, setOtherMinistryChecked] = useState<boolean>(false);

  useEffect(() => {
    if (sessionStorage.getItem('accessToken') === null) {
      fetch(`${process.env.NEXT_PUBLIC_GATEWAY_HOST}/auth/signup`, {
        method: 'POST',
        credentials: 'include',
      }).then(res =>
        res.json().then(data => {
          sessionStorage.setItem('accessToken', data.access_token);
        })
      );
    }

    fetch('https://geo.life.church/where')
      .then(res => res.json())
      .then((result: GeoWhere) => setGeoWhereData(result));
  }, []);

  useEffect(() => {
    const cachedOther = values.areaOfMinistries.find(area => {
      return !ministryData.areaOfMinistries.find(
        (a: SignupAreaOfMinistriesInput) => a.id === area.id
      );
    });
    if (cachedOther) {
      setOtherMinistry(cachedOther);
      setOtherMinistryChecked(true);
    }
  }, [ministryData, values.areaOfMinistries]);

  const toggleOtherMinistryChecked = () => {
    let areaOfMinistries;
    if (otherMinistryChecked) {
      areaOfMinistries = values.areaOfMinistries.filter(
        (m: SignupAreaOfMinistriesInput) => m.id !== otherMinistry.id
      );
    } else {
      areaOfMinistries = [...values.areaOfMinistries, otherMinistry];
    }
    setValues({
      ...values,
      areaOfMinistries,
    });
    setOtherMinistryChecked(!otherMinistryChecked);
  };

  const handleEmailConsentChange = () => {
    setValues({
      ...values,
      emailConsent: !values.emailConsent,
    });
  };

  const handleAreaOfMinistriesChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (ministryData) {
      const areaOfMinistryId = event.currentTarget.value;
      const ministry = ministryData.areaOfMinistries.find(
        (area: SignupAreaOfMinistriesInput) => area.id === areaOfMinistryId
      );
      if (ministry) {
        setErrors({
          ...errors,
          areaOfMinistries: undefined,
        });
        let areaOfMinistries;
        if (ministryChecked(ministry)) {
          areaOfMinistries = values.areaOfMinistries.filter(
            (m: SignupAreaOfMinistriesInput) => m.id !== ministry.id
          );
        } else {
          const { __typename: _stripped, ...min } = ministry;
          areaOfMinistries = [...values.areaOfMinistries, min];
        }
        setValues({
          ...values,
          areaOfMinistries,
        });
      }
    }
  };

  const ministryChecked = (ministry: SignupAreaOfMinistriesInput): boolean => {
    return !!values.areaOfMinistries.find(area => area.id === ministry.id);
  };

  const handleOtherMinistryLabelChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setErrors({
      ...errors,
      areaOfMinistries: undefined,
    });
    const newLabel = event.currentTarget.value;
    const newOtherMinistry = {
      ...otherMinistry,
      label: newLabel,
      key: newLabel.toLowerCase(),
    };
    setOtherMinistry(newOtherMinistry);
    if (otherMinistryChecked) {
      let areaOfMinistries = values.areaOfMinistries.filter(
        (area: SignupAreaOfMinistriesInput) => area.id !== otherMinistry.id
      );
      areaOfMinistries = [...areaOfMinistries, newOtherMinistry];
      setValues({
        ...values,
        areaOfMinistries,
      });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.persist();
    e.preventDefault();
    const localErrors = await validateForm();
    const hasLocalErrors = Object.values(localErrors).some(e => !!e);
    if (!hasLocalErrors) {
      clientCachedSubscriber(values);
      router.push('/signup/church');
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate={true}>
      <SignupGrid>
        <SignupGridItem mb={3}>
          <IGroupHeader>Create Your Account</IGroupHeader>
        </SignupGridItem>
        {Object.values(errors).some(value => !!value) && (
          <SignupGridItem>
            <FormErrors errors={errors as Record<string, string>} />
          </SignupGridItem>
        )}
        <SignupGridItem>
          <FancyInput
            size={InputSize.MD}
            label='First Name'
            name='firstName'
            value={values.firstName}
            onChange={handleChange}
            error={!!errors.firstName}
            hint={errors.firstName}
            required
          />
        </SignupGridItem>
        <SignupGridItem>
          <FancyInput
            size={InputSize.MD}
            label='Last Name'
            name='lastName'
            value={values.lastName}
            onChange={handleChange}
            error={!!errors.lastName}
            hint={errors.lastName}
            required
          />
        </SignupGridItem>
        <SignupGridItem>
          <FancyInput
            size={InputSize.MD}
            label='Nickname'
            name='nickname'
            value={values.nickname}
            onChange={handleChange}
            error={!!errors.nickname}
            context='This is the name people will see in chat. Must be at least 3 characters.'
            hint={errors.nickname}
            required
          />
        </SignupGridItem>
        <SignupGridItem>
          <FancyInput
            size={InputSize.MD}
            label='Email'
            name='email'
            value={values.email}
            onChange={handleChange}
            error={!!errors.email}
            hint={errors.email}
            required
          />
        </SignupGridItem>
        <SignupGridItem>
          <FancyInput
            size={InputSize.MD}
            label='Password'
            name='password'
            type='password'
            value={values.password}
            onChange={handleChange}
            error={!!errors.password}
            context='Must be at least 8 characters.'
            hint={errors.password}
            required
          />
        </SignupGridItem>
        <SignupGridItem>
          <IDetail>Area of Ministry*</IDetail>
          {errors.areaOfMinistries && values.areaOfMinistries.length === 0 && (
            <OXSmallParagraph mt={1} color={defaultColors.error.default}>
              Area of Ministry is required
            </OXSmallParagraph>
          )}
          <OSmallParagraph color={defaultColors.gray50}>
            Choose all that apply. You must select at least one option.
          </OSmallParagraph>
          <Box
            display='flex'
            border={`1px solid ${
              errors.areaOfMinistries ? defaultColors.error.default : defaultColors.gray30
            }`}
            borderRadius='4px'
            p={5}
            mt={3}
            flexDirection='column'
          >
            <Box display='flex'>
              <Box width='100%'>
                {ministryData &&
                  ministryData.areaOfMinistries
                    .slice(0, 4)
                    .map((area: SignupAreaOfMinistriesInput) => (
                      <Box display='flex' alignItems='center' mb={3} key={area.key}>
                        <input
                          type='checkbox'
                          name={`areaOfMinistries`}
                          value={area.id}
                          style={{ marginRight: '8px' }}
                          onChange={handleAreaOfMinistriesChange}
                          checked={ministryChecked(area)}
                          data-testid={`${area.key}-area`}
                        />
                        <IDetail>{area.label}</IDetail>
                      </Box>
                    ))}
                <Box display='flex' alignItems='center' mb={3}>
                  <input
                    type='checkbox'
                    name='other'
                    value={otherMinistry.id}
                    style={{ marginRight: '8px' }}
                    onChange={toggleOtherMinistryChecked}
                    checked={otherMinistryChecked}
                    data-testid='other-area'
                  />
                  <IDetail>Other</IDetail>
                </Box>
              </Box>
              <Box width='100%'>
                {ministryData &&
                  ministryData.areaOfMinistries
                    .slice(4, 8)
                    .map((area: SignupAreaOfMinistriesInput) => (
                      <Box display='flex' alignItems='center' mb={3} key={area.key}>
                        <input
                          type='checkbox'
                          name={area.key}
                          value={area.id}
                          style={{ marginRight: '8px' }}
                          onChange={handleAreaOfMinistriesChange}
                          checked={ministryChecked(area)}
                          data-testid={`${area.key}-area`}
                        />
                        <IDetail>{area.label}</IDetail>
                      </Box>
                    ))}
              </Box>
            </Box>
            {otherMinistryChecked && (
              <Input
                size={InputSize.MD}
                placeholder='Other*'
                value={otherMinistry.label}
                onChange={handleOtherMinistryLabelChange}
                error={!!errors.areaOfMinistries}
                hint={errors.areaOfMinistries}
              />
            )}
          </Box>
        </SignupGridItem>
        {geoWhereData?.gdpr === true && (
          <SignupGridItem>
            <Box display='flex'>
              <Checkbox
                name='consent'
                onChange={handleEmailConsentChange}
                checked={values.emailConsent}
              >
                Church Online Platform can email me about product updates and ways to enhance my
                online ministry
              </Checkbox>
            </Box>
          </SignupGridItem>
        )}
        <SignupGridItem mt={6}>
          <PrimaryButton size={ButtonSize.LG} type='submit'>
            Continue
          </PrimaryButton>
        </SignupGridItem>
      </SignupGrid>
    </form>
  );
};

export default UserSignupForm;
